import { useEffect, useRef, useState } from 'react';
import { menuItems, pickupLabel, sizeTitle, type LineConfig, type LineReward, type MenuItem, type OrderDto } from '@tikkas/shared';
import { ART, COIN, Svg, imageFor } from '../art/icons.js';
import { noteJustHappened, topupHappened } from '../lib/justHappened.js';
import { api } from '../lib/api.js';
import { newBuilder } from '../lib/bag.js';
import { fmt, hhmmLocal, num } from '../lib/format.js';
import { useNav } from '../lib/router.js';
import { emailBonusToCome, rememberPayChoice, rememberedPayChoice, useStore, welcomeToCome } from '../lib/store.js';
import { emailErrorText, welcomeLine } from '../lib/joining.js';
import { useLook } from '../lib/look.js';
import { Sheet, Spinner } from './ui.js';
import { TimeWheel, type WheelOption } from './TimeWheel.js';
import { NewCardForm, WalletButton, WalletMark, payNewCard, paySavedCard, walletUsable, type NewCardHandle, type PayablePending, type Wallets } from './Pay.js';
import { testToolsAllowed } from '../lib/platform.js';
import { LegalLinks } from '../screens/Legal.js';

export function PickupSheet() {
  const s = useStore();
  // Open on the day of the chosen time, else today while it still has times, else the next day that has any.
  const [day, setDay] = useState<number>(() => {
    const chosenDay = s.pickup.type === 'slot' ? s.pickupDays.findIndex((d) => d.slots.includes(s.pickup.type === 'slot' ? s.pickup.at : '')) : -1;
    if (chosenDay >= 0) return chosenDay;
    const t = s.pickupDays[0];
    if (t && (t.asapReadyAt || t.slots.length)) return 0;
    return Math.max(0, s.pickupDays.findIndex((d) => d.slots.length > 0));
  });
  const d = s.pickupDays[day];
  const chosen = s.pickup;
  // The wheel (Saif, 3 Nov 2026): the soonest time first, then every time on offer, five rows tall; one button confirms.
  const options: WheelOption[] = d ? [
    ...(d.asapReadyAt ? [{ key: 'asap', label: hhmmLocal(d.asapReadyAt), tag: 'Soonest' }] : []),
    ...d.slots.map((t) => ({ key: t, label: hhmmLocal(t) })),
  ] : [];
  const [sel, setSel] = useState<string>(() => (chosen.type === 'asap' ? 'asap' : chosen.type === 'slot' ? chosen.at : ''));
  const selKey = options.some((o) => o.key === sel) ? sel : (options[0]?.key ?? '');
  const choice = selKey === 'asap' ? { type: 'asap' as const } : { type: 'slot' as const, at: selKey };
  const changeDay = (i: number) => {
    setDay(i);
    const nd = s.pickupDays[i];
    setSel(nd?.asapReadyAt ? 'asap' : (nd?.slots[0] ?? ''));
  };
  const confirm = () => {
    if (!selKey) return;
    s.setPickup(choice);
    s.closeSheet();
  };
  // The days can arrive after the sheet opens (a cold start on ?sheet=pickup): move off a day with nothing to pick.
  useEffect(() => {
    const here = s.pickupDays[day];
    if (here && !here.asapReadyAt && !here.slots.length) {
      const i = s.pickupDays.findIndex((x) => x.asapReadyAt || x.slots.length);
      if (i >= 0 && i !== day) changeDay(i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.pickupDays]);
  return (
    <Sheet onClose={s.closeSheet}>
      <h2 className="h2">When will you collect?</h2>
      <div className="seg" style={{ gridTemplateColumns: `repeat(${Math.max(2, s.pickupDays.length)},minmax(0,1fr))` }}>
        {s.pickupDays.map((x, i) => <button type="button" key={x.dateKey} aria-pressed={day === i} onClick={() => changeDay(i)}>{x.label}</button>)}
      </div>
      {!d || (!d.open && !d.asapReadyAt) ? <p className="small muted" style={{ margin: 0 }}>{d?.label === 'Today' ? "We're closed for the rest of today. Pick another day above." : 'Closed that day. Pick another day above.'}</p> : (
        <div className="col" style={{ gap: 12 }}>
          <TimeWheel options={options} value={selKey} onChange={setSel} label="Collection time" />
          <button type="button" className="yellow" onClick={confirm} disabled={!selKey}>{selKey ? pickupLabel(choice, new Date(), s.pickupDays) : 'Pick a time'}</button>
        </div>
      )}
      <span className="small muted">Collect from {s.config?.site.address ?? '1 Great Titchfield Street, W1W 8AU'}.</span>
    </Sheet>
  );
}

export function pickupText(s: ReturnType<typeof useStore>): string {
  if (!s.pickupDays.length) return 'Collect';
  return pickupLabel(s.pickup, new Date(), s.pickupDays);
}

/** Pick a free main (opens the builder) or a free side / dessert (goes straight in the bag). */
export function PickRewardSheet({ reward, mainsOnly, dessertsOnly }: { reward: LineReward; mainsOnly: boolean; dessertsOnly: boolean }) {
  const s = useStore();
  const { navigate } = useNav();
  const menu = s.menuData?.menu;
  if (!menu) return null;
  const items = mainsOnly ? menuItems(menu).filter((i) => i.hasTikkas) : dessertsOnly ? menuItems(menu, 'desserts') : [...menuItems(menu, 'sides'), ...menuItems(menu, 'desserts')];
  const choose = (it: MenuItem) => {
    s.closeSheet();
    if (mainsOnly) {
      s.setBuilder(newBuilder(it.id, reward));
      navigate('/build');
    } else {
      s.addLine({ itemId: it.id, sizeIndex: 0, portions: [], sauces: [], toppingsOff: [], upgradeIds: [], reward });
    }
  };
  return (
    <Sheet onClose={s.closeSheet}>
      <h2 className="h2">{mainsOnly ? 'Pick your free main' : dessertsOnly ? 'Pick your free dessert' : 'Pick your free side or dessert'}</h2>
      <div className="col" style={{ gap: 8 }}>
        {items.map((it) => <button type="button" key={it.id} className="slot" disabled={!!it.soldOut} style={it.soldOut ? { opacity: 0.5 } : undefined} onClick={() => choose(it)}><b style={{ fontSize: 14 }}>{it.name}{mainsOnly ? ' · Regular' : ''}</b><span className="small muted">{it.soldOut ? 'Sold out today' : mainsOnly ? 'Free' : it.description}</span></button>)}
      </div>
    </Sheet>
  );
}

export function DeleteSheet() {
  const s = useStore();
  const { navigate } = useNav();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const c = s.me?.customer;
  const go = async () => {
    setBusy(true);
    try {
      await api('/api/me', { method: 'DELETE' });
      s.closeSheet();
      await s.signOut().catch(() => undefined);
      s.toast('Your account has been deleted');
      navigate('/', { root: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not delete');
      setBusy(false);
    }
  };
  return (
    <Sheet onClose={s.closeSheet} locked={busy}>
      <h2 className="h2">Delete your account?</h2>
      <p className="soft" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>This removes your account, order history and {num(c?.points ?? 0)} points. {c && c.creditPence > 0 && !c.reviewAccount ? `You still have ${fmt(c.creditPence)} of credit: spend it, or contact us to refund it, first.` : ''} This cannot be undone.</p>
      {err && <p className="err">{err}</p>}
      <button type="button" className="danger" disabled={busy} onClick={go}>{busy ? 'Deleting…' : 'Delete my account'}</button>
      <button type="button" className="plain" onClick={s.closeSheet}>Keep my account</button>
    </Sheet>
  );
}

export function SaveOrderSheet({ order }: { order: OrderDto }) {
  const s = useStore();
  const menu = s.menuData?.menu;
  const keep = order.lines.filter((l) => !l.config.reward && !l.config.membersPrice);
  const tk = keep.find((l) => menu?.items[l.itemId]?.hasTikkas);
  const [name, setName] = useState(() => (tk && menu ? sizeTitle(menu, tk.config) : ''));
  const [busy, setBusy] = useState(false);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api<{ saved: { name: string } }>(`/api/orders/${order.id}/save`, { body: { name } });
      await s.refreshMe();
      s.closeSheet();
      s.toast(`Saved as “${r.saved.name}” · find it on your home screen`);
    } catch (err) {
      s.toast(err instanceof Error ? err.message : 'Could not save');
      setBusy(false);
    }
  };
  return (
    <Sheet onClose={s.closeSheet}>
      <h2 className="h2">Save this order</h2>
      <p className="soft" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>Name it and it sits on your home screen, ready to reorder in one tap.</p>
      <div className="card savesum">{keep.map((l) => <div className="sum" key={l.id}><span>{l.title}{l.qty > 1 ? ` × ${l.qty}` : ''}<br /><span className="small muted">{l.description}</span></span><span className="tnum">{fmt(l.linePence)}</span></div>)}</div>
      <form className="codebox" onSubmit={save}><input id="savename" type="text" maxLength={24} autoComplete="off" placeholder="Name it, e.g. Lunch usual" aria-label="Order name" value={name} onChange={(e) => setName(e.target.value)} /><button type="submit" disabled={busy}>Save</button></form>
      <div className="chips grid2">{['Lunch usual', 'Friday treat', 'Office order', 'Gym day'].map((n) => <button type="button" key={n} className="chip" onClick={() => setName(n)}>{n}</button>)}</div>
    </Sheet>
  );
}

export function SpinRulesSheet() {
  const s = useStore();
  return (
    <Sheet onClose={s.closeSheet}>
      <h2 className="h2">Free spin rules</h2>
      <ul className="soft" style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.6 }}>
        <li>One free spin with each order. You never pay to spin and prices are never raised for it.</li>
        <li>Every spin wins points or food. Prizes are never cash and can't be swapped for cash.</li>
        <li>Points from a spin are added when you collect the order. A cancelled or refunded order loses its spin prize.</li>
        <li>Food prizes stay in your rewards for 30 days.</li>
        <li>Tikkas runs this promotion. Apple and Google are not sponsors and are not involved.</li>
      </ul>
      <button type="button" className="plain" onClick={s.closeSheet}>Close</button>
    </Sheet>
  );
}

export function PayingSheet({ label }: { label: string }) {
  const s = useStore();
  return (
    <Sheet onClose={() => undefined} locked>
      <div className="col" style={{ gap: 14, alignItems: 'center', padding: '20px 0 10px' }}><Spinner /><b>{label}</b>{testToolsAllowed(s.config?.testTools) && <span className="small muted">Test mode · nothing real is charged</span>}</div>
    </Sheet>
  );
}

/** Top up credit: pick an amount, then pay the same way as the bag (saved card, new card inline, or the wallet button). */
/** Store credit in and out, newest first (the Card tab's History link). */
export function CreditSheet() {
  const s = useStore();
  const [ledger, setLedger] = useState<{ kind: string; amountPence: number; note: string | null; at: string }[] | null>(null);
  useEffect(() => {
    api<{ ledger: { kind: string; amountPence: number; note: string | null; at: string }[] }>('/api/me/credit').then((r) => setLedger(r.ledger)).catch(() => setLedger([]));
  }, []);
  return (
    <Sheet onClose={s.closeSheet}>
      <h2 className="h2">Store credit</h2>
      <span style={{ fontFamily: 'var(--display)', fontSize: 36, lineHeight: 1 }} className="tnum">{fmt(s.me?.customer.creditPence ?? 0)}</span>
      {ledger === null ? <Spinner /> : ledger.length ? (
        <div className="ledger">{ledger.slice(0, 12).map((l, i) => <div key={i}><span className="col" style={{ gap: 2 }}><span style={{ fontWeight: 600 }}>{l.note ?? l.kind}</span><span className="small muted">{new Date(l.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span></span><span className="tnum" style={{ fontWeight: 600, color: l.amountPence > 0 ? 'var(--good)' : undefined }}>{l.amountPence > 0 ? '+' : '−'}{fmt(Math.abs(l.amountPence))}</span></div>)}</div>
      ) : <p className="small muted" style={{ margin: 0 }}>Nothing yet. Top up from the Card tab, or ask at the till.</p>}
      <button type="button" className="plain" onClick={s.closeSheet}>Close</button>
    </Sheet>
  );
}

export function TopupSheet({ amountPence }: { amountPence?: number | undefined } = {}) {
  const s = useStore();
  const opts = s.config?.rewards.topups ?? [];
  const pk = s.config?.stripe.publishableKey ?? '';
  const accountId = s.config?.site.stripeAccountId ?? '';
  const stripeOn = !!pk && !!accountId && !!s.config?.stripe.configured;
  const cards = s.me?.customer.paymentMethods ?? [];
  const [amt, setAmt] = useState(amountPence ?? opts[1]?.pricePence ?? 2000);
  const [method, setMethod] = useState<'saved' | 'new' | 'wallet'>(() => (cards.length ? 'saved' : 'new'));
  const [wallets, setWallets] = useState<Wallets | null>(null);
  const touched = useRef(false);
  const [pref] = useState(() => rememberedPayChoice());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardReady, setCardReady] = useState(false);
  const cardRef = useRef<NewCardHandle>(null);
  const pending = useRef<{ id: string; amountPence: number } | null>(null);
  const credit = s.me?.customer.creditPence ?? 0;
  const bonus = opts.find((o) => o.pricePence === amt)?.bonusPence ?? 0;
  const walletAllowed = stripeOn; // Apple Pay / Google Pay on the website and, through the native sheet, in the app (docs/32)
  const walletMaybe = walletAllowed && (wallets === null || walletUsable(wallets));
  const walletOn = walletUsable(wallets);
  const walletMode = method === 'wallet' && walletMaybe;
  const walletPending = walletMode && wallets === null;
  const useNewCard = method === 'new' || (method === 'saved' && !cards[0]);
  useEffect(() => {
    pending.current = null; // a new amount means a new top-up
    setError(null);
  }, [amt]);
  // Same order as the bag: what was tapped here, else the remembered choice, else saved card, wallet, new card.
  useEffect(() => {
    const valid = (m: typeof method) => (m === 'saved' ? !!cards[0] : m === 'wallet' ? walletMaybe : true);
    const prefM = pref?.kind === 'saved' ? 'saved' : pref?.kind === 'wallet' ? 'wallet' : null;
    let next: typeof method;
    if (touched.current && valid(method)) next = method;
    else if (prefM && valid(prefM)) next = prefM;
    else next = cards[0] ? 'saved' : walletMaybe ? 'wallet' : 'new';
    if (next !== method) setMethod(next);
  }, [cards.length, walletMaybe]); // eslint-disable-line react-hooks/exhaustive-deps
  const choose = (m: typeof method) => {
    touched.current = true;
    rememberPayChoice(m === 'saved' && cards[0] ? { kind: 'saved', id: cards[0].id } : m === 'wallet' ? { kind: 'wallet' } : { kind: 'new' });
    setMethod(m);
  };
  const createPayable = async (): Promise<PayablePending> => {
    if (pending.current && pending.current.amountPence === amt) return { kind: 'topup', id: pending.current.id, cardPence: amt, requiresPayment: true };
    const r = await api<{ topup: { id: string; amountPence: number } }>('/api/credit/topups', { body: { amountPence: amt, clientKey: `topup-${Date.now()}` } });
    pending.current = { id: r.topup.id, amountPence: r.topup.amountPence };
    return { kind: 'topup', id: r.topup.id, cardPence: r.topup.amountPence, requiresPayment: true };
  };
  const done = async () => {
    pending.current = null;
    s.closeSheet();
    await s.refreshMe();
    s.toast(`${fmt(amt + bonus)} added to your credit`);
    noteJustHappened(topupHappened(amt + bonus));
  };
  const pay = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (!stripeOn) throw new Error("Card payments aren't switched on here yet.");
      const p = await createPayable();
      if (!useNewCard && cards[0]) await paySavedCard(pk, accountId, p, cards[0].id);
      else {
        if (!cardRef.current) throw new Error('The card form is still loading');
        await payNewCard(pk, accountId, p, await cardRef.current.submit(), s.rememberCard);
      }
      await done();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Payment failed');
      setBusy(false);
    }
  };
  const foot = (
    <>
      {error && <p className="err" role="alert" style={{ margin: 0 }}>{error}</p>}
      {walletAllowed && <WalletButton pk={pk} accountId={accountId} amountPence={amt} selected={walletMode} createPayable={createPayable} onPaid={done} onError={setError} onAvailable={setWallets} onBusy={setBusy} />}
      {!walletMode && <button type="button" className="yellow" disabled={busy || !stripeOn || (useNewCard && !cardReady)} onClick={pay}>{busy ? 'One moment…' : `Add £${amt / 100}${bonus ? ` (+£${bonus / 100} bonus)` : ''}${!useNewCard && cards[0] ? ` · ${cards[0].label}` : ''}`}</button>}
    </>
  );
  // The App Review demo account never pays by card (the server refuses it), so it gets an explanation, not a card form.
  if (s.me?.customer.reviewAccount) {
    return (
      <Sheet onClose={s.closeSheet}>
        <h2 className="h2">Top up your credit</h2>
        <p className="soft" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>This demo account already holds {fmt(credit)} of store credit, so top-ups are switched off for it and no card is ever charged. Other customers top up here by card.</p>
        <button type="button" className="plain" onClick={s.closeSheet}>Close</button>
      </Sheet>
    );
  }
  return (
    <Sheet onClose={s.closeSheet} locked={busy} footer={foot}>
      <h2 className="h2">Top up your credit</h2>
      <div className="amts" role="radiogroup" aria-label="Top-up amount">{opts.map((o) => <button type="button" key={o.pricePence} role="radio" aria-checked={amt === o.pricePence} onClick={() => setAmt(o.pricePence)}>£{o.pricePence / 100}{o.bonusPence ? <span className="small" style={{ display: 'block', fontWeight: 600 }}>+£{o.bonusPence / 100} free</span> : null}</button>)}</div>
      <p className="soft" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>Credit never expires and earns 10 points for every £1 you spend with it. New balance: <b style={{ color: 'var(--ink)' }}>{fmt(credit + amt + bonus)}</b></p>
      {(cards[0] || walletOn || walletPending) && (
        <div className="card rows payrows" role="radiogroup" aria-label="Pay with">
          {walletOn && wallets && <button type="button" className="payrow" role="radio" aria-checked={method === 'wallet'} onClick={() => choose('wallet')}><span className="radio2" /><span className="grow col" style={{ gap: 2 }}><b>{wallets.applePay ? (wallets.googlePay ? 'Apple Pay or Google Pay' : 'Apple Pay') : 'Google Pay'}</b></span><span className="row" style={{ gap: 6 }}>{wallets.applePay && <WalletMark wallet="apple" />}{wallets.googlePay && <WalletMark wallet="google" />}</span></button>}
          {walletPending && <div className="payrow skel" aria-hidden="true"><span className="radio2" /><span className="grow col"><span className="skel-bar" /></span></div>}
          {cards[0] && <button type="button" className="payrow" role="radio" aria-checked={method === 'saved'} onClick={() => choose('saved')}><span className="radio2" /><span className="grow col" style={{ gap: 2 }}><b>{cards[0].label}</b><span className="s">Saved card</span></span><span className="paymark visa">{(cards[0].brand ?? 'card').toUpperCase()}</span></button>}
          <button type="button" className="payrow" role="radio" aria-checked={method === 'new'} onClick={() => choose('new')}><span className="radio2" /><span className="grow col" style={{ gap: 2 }}><b>{cards[0] ? 'Another card' : 'Card'}</b></span><span className="paymark card">CARD</span></button>
        </div>
      )}
      {useNewCard && stripeOn && <NewCardForm ref={cardRef} pk={pk} remember={s.rememberCard} onRemember={s.setRememberCard} onReady={() => setCardReady(true)} reveal={touched.current} />}
      {!stripeOn && <p className="err">Card payments aren't switched on here yet.</p>}
      <LegalLinks lead="Top-ups follow the" docs={['credit']} />
    </Sheet>
  );
}

/** First sign-in: what should we call you? Plus the marketing consent boxes (unticked by default). */
/**
 * Join our email list and get the bonus points (Saif, 24 Oct 2026): one incentivised, specific consent. The email goes on
 * the account and a confirmation link is sent with the list choice and the wording shown; tapping the link confirms the
 * email, records email marketing consent and pays the bonus once. Withdrawing later keeps the points. Plain "email for
 * receipts" lives in the profile sheet, with no bonus and no consent.
 */
export function EmailListSheet() {
  const s = useStore();
  const { t } = useLook();
  const u = s.me?.customer;
  const bonus = emailBonusToCome(s);
  const [email, setEmail] = useState(u?.email && !u.emailConfirmed ? u.email : '');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const wording = t('earn.email_list_text');
  const join = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await api('/api/me/email', { body: { email: email.trim(), newsletter: true, wording } });
      await s.refreshMe();
      setSent(email.trim());
    } catch (e2) {
      setErr(emailErrorText(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet onClose={s.closeSheet}>
      <h2 className="h2">{t('earn.email_list').replace('{bonus}', num(bonus))}</h2>
      {sent ? (
        <div className="col" style={{ gap: 12 }}>
          <p style={{ margin: 0 }}>Check your inbox: tap the link we sent to {sent} and {num(bonus)} points land on your card. It can take a minute; check spam too.</p>
          <button type="button" className="yellow" onClick={s.closeSheet}>Done</button>
        </div>
      ) : (
        <form className="col" style={{ gap: 12 }} onSubmit={join}>
          <p className="muted" style={{ margin: 0, fontSize: 14, lineHeight: 1.45 }}>{wording}</p>
          <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }}>Email<input className="txt-in" type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => { setEmail(e.target.value); setErr(null); }} /></label>
          {err && <p className="err" role="alert" style={{ margin: 0 }}>{err}</p>}
          <button type="submit" className="yellow" disabled={busy || !email.trim()}>{busy ? 'Sending…' : 'Join the list'}</button>
        </form>
      )}
    </Sheet>
  );
}

export function NameSheet({ onDone, welcome = false }: { onDone: () => void; welcome?: boolean }) {
  const s = useStore();
  const [first, setFirst] = useState(s.me?.customer.firstName ?? '');
  const [last, setLast] = useState(s.me?.customer.lastName ?? '');
  const [email, setEmail] = useState(s.me?.customer.email ?? '');
  const [busy, setBusy] = useState(false);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/api/me', { method: 'PATCH', body: { firstName: first, lastName: last, email: email || null } });
      await s.refreshMe();
      onDone();
    } catch (err) {
      s.toast(err instanceof Error ? err.message : 'Could not save');
      setBusy(false);
    }
  };
  return (
    <Sheet onClose={onDone}>
      {welcome && <p className="welcome" style={{ margin: 0 }}><Svg html={COIN(16)} /><span>Welcome to the Rewards Club{welcomeToCome(s) > 0 ? ` · ${welcomeLine(welcomeToCome(s))}` : ''}</span></p>}
      <h2 className="h2">What should we call you?</h2>
      <form className="col" style={{ gap: 12 }} onSubmit={save}>
        <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }}>First name<input className="txt-in" value={first} maxLength={40} autoComplete="given-name" onChange={(e) => setFirst(e.target.value)} /></label>
        <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }}>Last name (optional)<input className="txt-in" value={last} maxLength={40} autoComplete="family-name" onChange={(e) => setLast(e.target.value)} /></label>
        <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }}>Email for receipts (optional)<input className="txt-in" type="email" value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} /></label>
        <button type="submit" className="yellow" disabled={busy || !first.trim()}>Save</button>
      </form>
    </Sheet>
  );
}

/** Thumbnail for a bag or order line. */
export function LineThumb({ item }: { item: MenuItem | undefined; config?: LineConfig }) {
  if (!item) return <span className="lthumb art"><Svg html={ART.tots!} /></span>;
  if (item.image || item.photoUrl) return <img className="lthumb" src={imageFor(item.image, item.photoUrl)} alt="" />;
  return <span className="lthumb art"><Svg html={ART[item.id] ?? ART.tots!} /></span>;
}

